import React, { useEffect, useState } from 'react';
import Loader from '../../Shared/Loader';
import '../../../css/Refund.css';
import * as XLSX from 'xlsx';
import api from '../../../api/axiosConfig';
import { showToast } from '../../Shared/Toaster';

interface RefundItem {
  _id: string;
  razorpay_payment_id: string;
  user_id: string;
  product_id: string;
  status: string;
  final_amount: number;
  currency: string;
  payment_completed_at?: string;
  refunds?: RefundTransaction[];
  refund?: {
    refunded: boolean;
    refund_id?: string;
    refund_percentage?: number;
    actual_refund_percentage?: number;
    updated_refund_percentage?: number;
    refund_amount?: number;
    platform_fee_deducted?: number;
    refund_status?: string;
    refund_created_at?: string;
    refund_reason?: string;
  };
  created_at: string;
  updated_at: string;
}

interface RefundTransaction {
  _id?: string;
  refund_id: string;
  refund_percentage: number;
  actual_refund_percentage?: number;
  updated_refund_percentage?: number;
  refund_amount: number;
  refund_status?: string;
  refund_created_at?: string;
  refund_reason?: string;
}

interface RefundPolicy {
  _id?: string;
  refund_policy_id?: string;
  policy_key?: string;
  // New schema fields
  n1_days?: number;
  within_n1_days_percentage?: number;
  after_n1_until_n2_percentage?: number;
  n2_days?: number;
  within_n2_days_of_screening_percentage?: number;
  on_screening_date_percentage?: number;
  // Legacy fields (kept for fallback display)
  within_3_days_percentage?: number;
  after_3_days_until_screening_percentage?: number;
  created_by_user_id?: string;
  updated_by_user_id?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

const Refund: React.FC = () => {
  const [refunds, setRefunds] = useState<RefundItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);

  // Refund Policy States — new N1/N2 schema
  const [n1Days, setN1Days] = useState<string>('3');
  const [withinN1Pct, setWithinN1Pct] = useState<string>('100');
  const [afterN1UntilN2Pct, setAfterN1UntilN2Pct] = useState<string>('75');
  const [n2Days, setN2Days] = useState<string>('5');
  const [withinN2Pct, setWithinN2Pct] = useState<string>('50');
  const [onScreeningPct, setOnScreeningPct] = useState<string>('0');

  const [savingPolicy, setSavingPolicy] = useState<boolean>(false);
  const [loadingPolicy, setLoadingPolicy] = useState<boolean>(true);
  const [activePolicy, setActivePolicy] = useState<RefundPolicy | null>(null);
  const [policySuccess, setPolicySuccess] = useState<string | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const recordsPerPage = 10;

  useEffect(() => {
    fetchRefunds();
    fetchRefundPolicy();
  }, []);

  // Fetch active refund policy
  const fetchRefundPolicy = async () => {
    setLoadingPolicy(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      let res;
      try {
        res = await api.payment_api.get('/refund-policy', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      } catch {
        res = await api.payment_api.get('/pricing/refund-policy', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      }

      if (res.data && res.data.policy) {
        const p = res.data.policy;
        setActivePolicy(p);
        // Populate form fields from API response (new schema)
        setN1Days(String(p.n1_days ?? 3));
        setWithinN1Pct(String(p.within_n1_days_percentage ?? p.within_3_days_percentage ?? 100));
        setAfterN1UntilN2Pct(String(p.after_n1_until_n2_percentage ?? p.after_3_days_until_screening_percentage ?? 75));
        setN2Days(String(p.n2_days ?? 5));
        setWithinN2Pct(String(p.within_n2_days_of_screening_percentage ?? 50));
        setOnScreeningPct(String(p.on_screening_date_percentage ?? 0));
      }
    } catch (err) {
      console.error('Error fetching refund policy:', err);
    } finally {
      setLoadingPolicy(false);
    }
  };

  // Handle Save / Update Refund Policy
  const handlePolicySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPolicy(true);
    setPolicySuccess(null);

    const adminData = sessionStorage.getItem("backendResponse");
    const admin = adminData ? JSON.parse(adminData) : null;
    const updatedByUserId = admin?._id || "admin-user-id";

    const payload = {
      n1_days: parseInt(n1Days) || 3,
      within_n1_days_percentage: parseFloat(withinN1Pct) || 0,
      after_n1_until_n2_percentage: parseFloat(afterN1UntilN2Pct) || 0,
      n2_days: parseInt(n2Days) || 5,
      within_n2_days_of_screening_percentage: parseFloat(withinN2Pct) || 0,
      on_screening_date_percentage: parseFloat(onScreeningPct) || 0,
      updated_by_user_id: updatedByUserId,
    };

    try {
      const token = sessionStorage.getItem("accessToken");
      const headers = token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };

      try {
        await api.payment_api.post('/refund-policy', payload, { headers });
      } catch {
        await api.payment_api.post('/pricing/refund-policy', payload, { headers });
      }

      setPolicySuccess("Refund policy updated successfully!");
      showToast("Refund policy updated successfully!", 'success');
      setTimeout(() => setPolicySuccess(null), 3000);

      fetchRefundPolicy();
    } catch (err: any) {
      console.error('Error updating refund policy:', err);
      const errMsg = err.response?.data?.message || "Failed to update refund policy.";
      showToast(errMsg, 'error');
    } finally {
      setSavingPolicy(false);
    }
  };

  const fetchRefunds = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.payment_api.get('/payments/refunds/all', {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.data && res.data.refunds) {
        const mappedRefunds = res.data.refunds.flatMap((payment: RefundItem) => {
          if (payment.refunds?.length) {
            return payment.refunds.map((refund) => ({
              ...payment,
              _id: `${payment._id}-${refund._id || refund.refund_id}`,
              refund,
            }));
          }

          // Keep compatibility with the previous singular refund response shape.
          return payment.refund?.refund_id ? [payment] : [];
        });

        setRefunds(mappedRefunds);
      } else {
        setRefunds([]);
      }
    } catch (error) {
      console.error('Error fetching refunds:', error);
      setRefunds([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const filterRefundsByDate = (items: RefundItem[], filter: string | null) => {
    if (!filter) return items;
    const today = new Date();
    let startDate = new Date();
    switch (filter) {
      case 'last2days':
        startDate.setDate(today.getDate() - 2);
        break;
      case 'last7days':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'last30days':
        startDate.setDate(today.getDate() - 30);
        break;
      default:
        return items;
    }
    return items.filter(item => {
      const refundDate = new Date(item.refund?.refund_created_at || item.updated_at);
      return refundDate >= startDate && refundDate <= today;
    });
  };

  // Filter refunds by search term and date
  const filteredRefunds = filterRefundsByDate(
    refunds.filter(item => {
      const term = searchTerm.toLowerCase();
      const paymentId = (item.razorpay_payment_id || '').toLowerCase();
      const refundId = (item.refund?.refund_id || '').toLowerCase();
      const userId = (item.user_id || '').toLowerCase();
      const status = (item.status || '').toLowerCase();
      const reason = (item.refund?.refund_reason || '').toLowerCase();

      return (
        paymentId.includes(term) ||
        refundId.includes(term) ||
        userId.includes(term) ||
        status.includes(term) ||
        reason.includes(term)
      );
    }),
    selectedFilter
  );

  // Pagination Logic
  const totalPages = Math.ceil(filteredRefunds.length / recordsPerPage) || 1;
  const startIndex = (currentPage - 1) * recordsPerPage;
  const paginatedRefunds = filteredRefunds.slice(startIndex, startIndex + recordsPerPage);

  const handleExportToExcel = () => {
    const dataToExport = filteredRefunds.map((item, idx) => ({
      'No.': idx + 1,
      'Payment ID': item.razorpay_payment_id,
      'User ID': item.user_id,
      'Refund ID': item.refund?.refund_id || '—',
      'Original Amount': `₹${item.final_amount}`,
      'Refund Amount': `₹${item.refund?.refund_amount ?? item.final_amount}`,
      'Actual Refund %': item.refund?.actual_refund_percentage !== undefined ? `${item.refund.actual_refund_percentage}%` : '—',
      'Updated Refund %': item.refund?.updated_refund_percentage !== undefined ? `${item.refund.updated_refund_percentage}%` : `${item.refund?.refund_percentage ?? 100}%`,
      'Status': item.status,
      'Refund Date': formatDate(item.refund?.refund_created_at || item.updated_at),
      'Reason': item.refund?.refund_reason || '—',
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(dataToExport);
    XLSX.utils.book_append_sheet(wb, ws, 'Refund List');
    XLSX.writeFile(wb, 'refund_list.xlsx');
  };

  const renderStatusBadge = (status: string) => {
    const upper = (status || '').toUpperCase();
    let badgeClass = 'badge blue';

    if (upper.includes('SUCCESS') || upper === 'REFUND') {
      badgeClass = 'badge green';
    } else if (upper.includes('PARTIAL')) {
      badgeClass = 'badge purple';
    } else if (upper.includes('FAILED')) {
      badgeClass = 'badge red';
    } else if (upper.includes('PENDING') || upper.includes('INITIATED') || upper.includes('REQUESTED')) {
      badgeClass = 'badge yellow';
    }

    return <span className={badgeClass}>{status}</span>;
  };

  return (
    <div className="refund-container">
      {/* ================================
          REFUND POLICY SECTION
      ================================= */}
      <div className="refund-policy-section">
        <h2 className="section-title">Set Refund Policy</h2>

        <div className="refund-policy-grid">
          {/* Form to update policy */}
          <div className="refund-policy-form-card">
            <form onSubmit={handlePolicySubmit}>

              {/* Row 1: < N1 days of payment */}
              <div className="policy-form-row">
                <label className="policy-label">
                  {"< "}
                  <input
                    type="number"
                    min="1"
                    className="policy-inline-days-input"
                    value={n1Days}
                    onChange={(e) => setN1Days(e.target.value)}
                    required
                  />
                  {" days of payment"}
                </label>
                <div className="percent-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={withinN1Pct}
                    onChange={(e) => setWithinN1Pct(e.target.value)}
                    required
                  />
                  <span>%</span>
                </div>
              </div>

              {/* Row 2: > N1 days upto N2 days from screening date */}
              <div className="policy-form-row">
                <label className="policy-label">
                  {"> "}
                  <span className="policy-inline-days-display">{n1Days || "N1"}</span>
                  {" days upto "}
                  <span className="policy-inline-days-display">{n2Days || "N2"}</span>
                  {" days from screening date"}
                </label>
                <div className="percent-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={afterN1UntilN2Pct}
                    onChange={(e) => setAfterN1UntilN2Pct(e.target.value)}
                    required
                  />
                  <span>%</span>
                </div>
              </div>

              {/* Row 3: N2 days from screening upto screening date */}
              <div className="policy-form-row">
                <label className="policy-label">
                  <input
                    type="number"
                    min="1"
                    className="policy-inline-days-input"
                    value={n2Days}
                    onChange={(e) => setN2Days(e.target.value)}
                    required
                  />
                  {" days from screening upto screening date"}
                </label>
                <div className="percent-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={withinN2Pct}
                    onChange={(e) => setWithinN2Pct(e.target.value)}
                    required
                  />
                  <span>%</span>
                </div>
              </div>

              {/* Row 4: Scheduled screening date */}
              <div className="policy-form-row">
                <label className="policy-label">Scheduled screening date & Beyond</label>
                <div className="percent-input-wrapper">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={onScreeningPct}
                    onChange={(e) => setOnScreeningPct(e.target.value)}
                    required
                  />
                  <span>%</span>
                </div>
              </div>

              <button type="submit" className="save-policy-btn" disabled={savingPolicy}>
                {savingPolicy ? "Saving..." : "Save Refund Policy"}
              </button>

              {policySuccess && <p className="policy-success">{policySuccess}</p>}
            </form>
          </div>

          {/* Active Refund Policy Card on Right */}
          <div className="active-policy-card">
            <h3 className="active-policy-title">Active Refund Policy</h3>

            {loadingPolicy ? (
              <Loader inline text="Loading policy..." />
            ) : activePolicy ? (
              <div className="active-policy-details">
                <div className="active-policy-item">
                  <span className="item-key">{"< "}{activePolicy.n1_days ?? 3} days of payment:</span>
                  <span className="item-val">{activePolicy.within_n1_days_percentage ?? activePolicy.within_3_days_percentage ?? "—"}%</span>
                </div>

                <div className="active-policy-item">
                  <span className="item-key">{">"} {activePolicy.n1_days ?? 3} days upto {activePolicy.n2_days ?? 5} days from screening date:</span>
                  <span className="item-val">{activePolicy.after_n1_until_n2_percentage ?? activePolicy.after_3_days_until_screening_percentage ?? "—"}%</span>
                </div>

                <div className="active-policy-item">
                  <span className="item-key">{activePolicy.n2_days ?? 5} days from screening upto screening date:</span>
                  <span className="item-val">{activePolicy.within_n2_days_of_screening_percentage ?? "—"}%</span>
                </div>

                <div className="active-policy-item">
                  <span className="item-key">Scheduled screening date:</span>
                  <span className="item-val">{activePolicy.on_screening_date_percentage ?? "—"}%</span>
                </div>

                {activePolicy.updated_at && (
                  <div className="policy-updated-info">
                    <span>Last Updated: {new Date(activePolicy.updated_at).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>
            ) : (
              <p className="no-policy-text">No active refund policy configured yet.</p>
            )}
          </div>
        </div>
      </div>

      {/* ================================
          REFUND LIST SECTION
      ================================= */}
      <div className="header">
        <h2>Refund List</h2>
      </div>

      {/* Controls: Search, Filter, Export */}
      <div className="coupon-controls">
        <input
          type="text"
          placeholder="Search Payment ID, Refund ID, User or Status..."
          className="search-box"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />

        <div className="filter-export-div">
          <div className="filter-container">
            <button
              className="filter-button"
              onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#131718ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16l-6 8v6l-4 2v-8L4 4z" />
              </svg>
              Filter
              {selectedFilter && <span className="active-filter-dot"></span>}
            </button>

            {showFilterDropdown && (
              <div className="filter-dropdown">
                {['last2days', 'last7days', 'last30days'].map((f) => (
                  <div
                    key={f}
                    className={`filter-option ${selectedFilter === f ? 'active' : ''}`}
                    onClick={() => {
                      setSelectedFilter(f);
                      setShowFilterDropdown(false);
                      setCurrentPage(1);
                    }}
                  >
                    {f === 'last2days' ? 'Last 2 days' : f === 'last7days' ? 'Last 7 days' : 'Last 30 days'}
                  </div>
                ))}
                {selectedFilter && (
                  <div
                    className="filter-option clear"
                    onClick={() => {
                      setSelectedFilter(null);
                      setShowFilterDropdown(false);
                    }}
                  >
                    Clear filter
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="export-button" onClick={handleExportToExcel}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#312c2cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="2" ry="2" />
              <rect x="14" y="3" width="7" height="7" rx="2" ry="2" />
              <rect x="14" y="14" width="7" height="7" rx="2" ry="2" />
              <rect x="3" y="14" width="7" height="7" rx="2" ry="2" />
            </svg>
            Export
          </button>
        </div>
      </div>

      {/* Refund List Table */}
      <div className="coupon-container">
        <div className="table-wrapper">
          <table className="coupon-table">
            <thead>
              <tr>
                <th>NO.</th>
                <th>Payment ID</th>
                <th>User ID</th>
                <th>Refund ID</th>
                <th>Refund Amount</th>
                <th>Actual Refund %</th>
                <th>Updated Refund %</th>
                <th>Refund Date</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center' }}>
                    <Loader inline text="Loading Refund Records..." />
                  </td>
                </tr>
              ) : paginatedRefunds.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center' }}>
                    No refund records found.
                  </td>
                </tr>
              ) : (
                paginatedRefunds.map((item, idx) => (
                  <tr key={item._id}>
                    <td>{startIndex + idx + 1}</td>
                    <td>{item.razorpay_payment_id}</td>
                    <td>{item.user_id}</td>
                    <td>{item.refund?.refund_id || '—'}</td>
                    <td>₹{item.refund?.refund_amount ?? item.final_amount}</td>
                    <td>{item.refund?.actual_refund_percentage !== undefined ? `${item.refund.actual_refund_percentage}%` : '—'}</td>
                    <td>{item.refund?.updated_refund_percentage !== undefined ? `${item.refund.updated_refund_percentage}%` : `${item.refund?.refund_percentage ?? 100}%`}</td>
                    <td>{formatDate(item.refund?.refund_created_at || item.updated_at)}</td>
                    <td>{renderStatusBadge(item.status)}</td>
                    <td>{item.refund?.refund_reason || '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <span>
              Showing {paginatedRefunds.length > 0 ? `${startIndex + 1}-${startIndex + paginatedRefunds.length}` : '0'} of {filteredRefunds.length} refunds
            </span>
            <div className="pagination-buttons">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i + 1}
                  className={currentPage === i + 1 ? 'active' : ''}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}

              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Refund;
